// DocsExplorerSidebar — the Docs Hub binding of the shared ExplorerSidebar.
// Supplies the docs action vocabulary (new doc / folder, rename, move, copy,
// paste, delete) and maps the page's path selection onto ExplorerSelection.

import {
  ClipboardPaste,
  Copy,
  FilePlus,
  FileText,
  FolderInput,
  FolderPlus,
  Pencil,
  Scissors,
  Trash2,
  Undo2
} from 'lucide-react'
import { useMemo, type ReactNode } from 'react'
import {
  isDemoMode,
  isMarkdownDoc,
  type Doc,
  type DocFolder
} from '@axilon-apps/domains-ontology-ui-state'

import {
  explorerModShortcut,
  ExplorerSidebar,
  type ExplorerClipboardEntry,
  type ExplorerHeaderButton,
  type ExplorerMenuEntry,
  type ExplorerSelection,
  type ExplorerTreeNode
} from '../../components/explorer'
import { DOCS_EXPLORER_OPEN_KEY } from './explorer-paths'
import {
  INGESTED_DOCUMENTS_FOLDER_PATH,
  isIngestedDocumentsPath,
  withIngestedDocumentsVirtual
} from './ingested-documents-virtual'

export type DocsSidebarAction =
  | { kind: 'create-doc'; parentPath: string }
  | { kind: 'create-folder'; parentPath: string }
  | { kind: 'rename-folder'; path: string; name: string; folder?: DocFolder }
  | { kind: 'delete-folder'; path: string; name: string; folder?: DocFolder }
  | { kind: 'move-folder'; path: string; name: string }
  | { kind: 'move-doc'; doc: Doc }
  | { kind: 'drop-folder'; fromPath: string; toParentPath: string }
  | { kind: 'drop-doc'; doc: Doc; toParentPath: string }
  | { kind: 'copy-folder'; path: string; name: string }
  | { kind: 'cut-folder'; path: string; name: string }
  | { kind: 'copy-doc'; doc: Doc }
  | { kind: 'cut-doc'; doc: Doc }
  | { kind: 'paste'; parentPath: string }
  | { kind: 'undo' }
  | { kind: 'rename-doc'; doc: Doc }
  | { kind: 'delete-doc'; doc: Doc }

export interface DocsExplorerSidebarProps {
  folders: DocFolder[]
  docs: Doc[]
  selectedId: string | null
  selectedFolder: string | null
  expanded: Set<string>
  onToggle: (path: string) => void
  onSelectDoc: (id: string) => void
  onSelectFolder: (path: string) => void
  onAction: (action: DocsSidebarAction) => void
  filter: string
  onFilterChange: (filter: string) => void
  isLoading?: boolean
  createParentPath?: string
  clipboard?: ExplorerClipboardEntry | null
  undoLabel?: string | null
  /**
   * Optional content rendered above the sidebar header — used by the
   * Documents/Templates tab strip so the toggle sits at the very top of the
   * explorer column.
   */
  topSlot?: ReactNode
}

const toExplorerSelection = (
  selectedId: string | null,
  selectedFolder: string | null
): ExplorerSelection => {
  if (selectedId !== null) return { kind: 'item', id: selectedId }
  if (selectedFolder !== null) return { kind: 'folder', path: selectedFolder }
  return { kind: 'none' }
}

const DocsExplorerSidebar = ({
  folders,
  docs,
  selectedId,
  selectedFolder,
  expanded,
  onToggle,
  onSelectDoc,
  onSelectFolder,
  onAction,
  filter,
  onFilterChange,
  isLoading,
  createParentPath = '',
  clipboard = null,
  undoLabel = null,
  topSlot
}: DocsExplorerSidebarProps) => {
  const explorerFolders = useMemo(() => withIngestedDocumentsVirtual(folders), [folders])
  const canWrite = !isDemoMode()
  const canPaste = canWrite && clipboard !== null
  const copyShortcut = explorerModShortcut('C')
  const cutShortcut = explorerModShortcut('X')
  const pasteShortcut = explorerModShortcut('V')
  const undoShortcut = explorerModShortcut('Z')
  const dimmedFolderPaths =
    clipboard?.mode === 'cut' && clipboard.kind === 'folder' ? new Set([clipboard.path]) : undefined
  const dimmedItemIds =
    clipboard?.mode === 'cut' && clipboard.kind === 'item' ? new Set([clipboard.id]) : undefined

  const folderMenu = (node: ExplorerTreeNode<DocFolder, Doc>): ExplorerMenuEntry[] => {
    if (!canWrite || isIngestedDocumentsPath(node.path)) return []
    const displayName = node.folder?.name ?? node.name
    return [
      {
        icon: FilePlus,
        label: 'New doc here',
        onSelect: () => onAction({ kind: 'create-doc', parentPath: node.path })
      },
      {
        icon: FolderPlus,
        label: 'New folder here',
        onSelect: () => onAction({ kind: 'create-folder', parentPath: node.path })
      },
      {
        icon: Pencil,
        label: 'Rename',
        separatorBefore: true,
        onSelect: () =>
          onAction({
            kind: 'rename-folder',
            path: node.path,
            name: displayName,
            folder: node.folder
          })
      },
      {
        icon: FolderInput,
        label: 'Move to…',
        onSelect: () => onAction({ kind: 'move-folder', path: node.path, name: displayName })
      },
      {
        icon: Copy,
        label: 'Copy',
        shortcut: copyShortcut,
        onSelect: () => onAction({ kind: 'copy-folder', path: node.path, name: displayName })
      },
      {
        icon: Scissors,
        label: 'Cut',
        shortcut: cutShortcut,
        onSelect: () => onAction({ kind: 'cut-folder', path: node.path, name: displayName })
      },
      {
        icon: ClipboardPaste,
        label: 'Paste',
        shortcut: pasteShortcut,
        disabled: !canPaste,
        onSelect: () => onAction({ kind: 'paste', parentPath: node.path })
      },
      {
        icon: Trash2,
        label: 'Delete',
        variant: 'destructive',
        separatorBefore: true,
        onSelect: () =>
          onAction({
            kind: 'delete-folder',
            path: node.path,
            name: displayName,
            folder: node.folder
          })
      }
    ]
  }

  const headerButtons: ExplorerHeaderButton[] = canWrite
    ? [
        {
          icon: FilePlus,
          label: 'New doc',
          onClick: () => onAction({ kind: 'create-doc', parentPath: createParentPath })
        },
        {
          icon: FolderPlus,
          label: 'New folder',
          onClick: () => onAction({ kind: 'create-folder', parentPath: createParentPath })
        }
      ]
    : []

  const rootMenu: ExplorerMenuEntry[] = canWrite
    ? [
        {
          icon: FilePlus,
          label: 'New doc',
          onSelect: () => onAction({ kind: 'create-doc', parentPath: '' })
        },
        {
          icon: FolderPlus,
          label: 'New folder',
          onSelect: () => onAction({ kind: 'create-folder', parentPath: '' })
        },
        {
          icon: ClipboardPaste,
          label: 'Paste',
          shortcut: pasteShortcut,
          disabled: !canPaste,
          onSelect: () => onAction({ kind: 'paste', parentPath: '' })
        },
        {
          icon: Undo2,
          label: undoLabel ?? 'Undo',
          shortcut: undoShortcut,
          disabled: undoLabel === null,
          onSelect: () => onAction({ kind: 'undo' })
        }
      ]
    : []

  return (
    <ExplorerSidebar<DocFolder, Doc>
      heading="Documents"
      ariaLabel="Documents explorer"
      folders={explorerFolders}
      items={docs}
      itemIcon={FileText}
      itemTrailing={(doc) =>
        isMarkdownDoc(doc) || !doc.format ? null : (
          <span className="shrink-0 text-10 uppercase tracking-wide text-ink-3">{doc.format}</span>
        )
      }
      testIdPrefix="docs-tree"
      selection={toExplorerSelection(selectedId, selectedFolder)}
      expanded={expanded}
      filter={filter}
      isLoading={isLoading}
      emptyHint={
        canWrite
          ? 'No documents yet. Right-click to add one, or ask the agent to draft it.'
          : 'Documents are browse-only in this guided demo.'
      }
      headerButtons={headerButtons}
      folderMenu={folderMenu}
      itemMenu={(doc) =>
        canWrite && !isIngestedDocumentsPath(doc.path)
          ? [
              {
                icon: Pencil,
                label: 'Rename',
                onSelect: () => onAction({ kind: 'rename-doc', doc })
              },
              {
                icon: FolderInput,
                label: 'Move to…',
                onSelect: () => onAction({ kind: 'move-doc', doc })
              },
              {
                icon: Copy,
                label: 'Copy',
                shortcut: copyShortcut,
                onSelect: () => onAction({ kind: 'copy-doc', doc })
              },
              {
                icon: Scissors,
                label: 'Cut',
                shortcut: cutShortcut,
                onSelect: () => onAction({ kind: 'cut-doc', doc })
              },
              {
                icon: Trash2,
                label: 'Delete',
                variant: 'destructive',
                separatorBefore: true,
                onSelect: () => onAction({ kind: 'delete-doc', doc })
              }
            ]
          : []
      }
      rootMenu={rootMenu}
      onToggle={onToggle}
      onSelectFolder={onSelectFolder}
      onSelectItem={onSelectDoc}
      onFilterChange={onFilterChange}
      onMoveItem={
        canWrite
          ? (doc, toParentPath) => onAction({ kind: 'drop-doc', doc, toParentPath })
          : undefined
      }
      onMoveFolder={
        canWrite
          ? (fromPath, toParentPath) => onAction({ kind: 'drop-folder', fromPath, toParentPath })
          : undefined
      }
      isLockedFolder={isIngestedDocumentsPath}
      isLockedItem={(doc) => isIngestedDocumentsPath(doc.path)}
      folderTrailing={(node) =>
        node.path === INGESTED_DOCUMENTS_FOLDER_PATH ? (
          <span className="shrink-0 rounded-full bg-brand-soft px-1.5 py-px text-9 font-medium uppercase tracking-wide text-brand">
            Auto
          </span>
        ) : null
      }
      navigationMode="drilldown"
      pinnedFolderPaths={[INGESTED_DOCUMENTS_FOLDER_PATH]}
      dimmedFolderPaths={dimmedFolderPaths}
      dimmedItemIds={dimmedItemIds}
      topSlot={topSlot}
      collapseStorageKey={DOCS_EXPLORER_OPEN_KEY}
    />
  )
}

export default DocsExplorerSidebar
